import {useContext, useMemo} from 'react';

import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {type Account} from '../../../components/Search/SearchNav/types';
import {envIsServerDeployment} from '../../../config';
import {useRegistryMembersQuery} from '../../../generated/graphql';
import {getViewerOrganizationMemberRole} from '../../../pages/Billing/AccountSettings/util';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useViewer} from '../../../state/viewer/hooks';
import {useAdminModeActive} from '../../../util/admin';
import {propagateErrorsContext} from '../../../util/errors';
import {type OrganizationMemberRole} from '../../MembersTable/util';

export type AccountSettingsData = {
  settingsLoading: boolean;
  isViewerInternalAdmin: boolean;
  viewerMemberRole?: OrganizationMemberRole;
  selectedAccount: Account | null;
};

export const useRegistryAccountSettings = (): AccountSettingsData => {
  const viewer = useViewer();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isViewerInternalAdmin = useAdminModeActive();

  // This is making the assumption that its impossible to get to this page on a personal account since we aren't
  // checking if the org selector is potentially on a personal account
  const orgName = envIsServerDeployment
    ? DEFAULT_ONPREM_ORGANIZATION_NAME
    : selectedAccount?.name;

  const {data: organizationMembers, loading: organizationQueryLoading} =
    useRegistryMembersQuery({
      variables: {orgName: orgName as string},
      skip: orgName == null,
      context: propagateErrorsContext(),
    });

  const viewerMemberRole = getViewerOrganizationMemberRole(
    viewer,
    organizationMembers?.organization?.members
  );

  const state = useMemo(() => {
    return {
      settingsLoading: organizationQueryLoading,
      viewerMemberRole,
      isViewerInternalAdmin,
      selectedAccount,
    };
  }, [
    viewerMemberRole,
    isViewerInternalAdmin,
    selectedAccount,
    organizationQueryLoading,
  ]);

  return state;
};
